import { crearTabla, mostrarToast } from '@plataforma/ui';

import {
  crearBeneficio,
  rechazarBeneficio,
  listarBeneficiosBackoffice,
} from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  estadoCarga,
  estadoError,
  ESTADOS_BENEFICIO,
  fecha,
  fechaHora,
  motivoRapido,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

const TIPOS_BENEFICIO = [
  'acceso Gratuito',
  'extensión de trial',
  'descuento porcentual',
  'descuento fijo',
  'período bonificado',
  'acceso premium temporal',
  'otro',
];

export async function vistaBeneficios(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/beneficios');
  const estadoVista = { empresaId: '', estado: '', pagina: 1 };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Beneficios</h1>
        <p class="pf-texto-suave">Períodos de acceso sin costo otorgados a empresas por super admin.</p>
      </div>
      <div class="pf-page-header__acciones">
        <button type="button" class="pf-boton pf-boton--primario" id="pf-nuevo-beneficio">
          Nuevo beneficio
        </button>
      </div>
    </div>
    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-beneficios">
      <div class="pf-campo">
        <label for="pf-estado-beneficios">Estado</label>
        <select id="pf-estado-beneficios">
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="aprobado">Aprobado</option>
          <option value="rechazado">Rechazado</option>
          <option value="expirado">Expirado</option>
        </select>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Filtrar</button>
    </form>
    <div id="pf-beneficios-tabla">${estadoCarga()}</div>
  `;

  contenido.querySelector('#pf-filtros-beneficios').addEventListener('submit', async (e) => {
    e.preventDefault();
    estadoVista.estado = contenido.querySelector('#pf-estado-beneficios').value;
    estadoVista.pagina = 1;
    await cargar(contenido, estadoVista);
  });

  contenido.querySelector('#pf-nuevo-beneficio').addEventListener('click', () => {
    abrirModalCrear(contenido);
  });

  await cargar(contenido, estadoVista);
}

async function cargar(contenido, estadoVista) {
  const destino = contenido.querySelector('#pf-beneficios-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error, count } = await listarBeneficiosBackoffice(estadoVista);
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar los beneficios.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay beneficios registrados.',
      columnas: [
        { titulo: 'Empresa', render: (f) => f.empresa?.nombre ?? '—' },
        { titulo: 'Tipo', render: (f) => f.tipo },
        { titulo: 'Descripción', render: (f) => f.descripcion?.substring(0, 60) ?? '—' },
        { titulo: 'Inicio', render: (f) => fecha(f.fecha_inicio) },
        { titulo: 'Fin', render: (f) => fecha(f.fecha_fin) },
        { titulo: 'Estado', render: (f) => crearBadge(ESTADOS_BENEFICIO, f.estado) },
        { titulo: 'Creado', render: (f) => fechaHora(f.created_at) },
        {
          titulo: 'Acciones',
          render: (f) => {
            if (f.estado !== 'pendiente') return '';
            const div = document.createElement('div');
            div.style.cssText = 'display:flex;gap:6px;';

            const btnAprobar = document.createElement('button');
            btnAprobar.type = 'button';
            btnAprobar.className = 'pf-boton pf-boton--primario pf-boton--chico';
            btnAprobar.textContent = 'Aprobar';
            btnAprobar.addEventListener('click', async () => {
              const { error } = await import('../api/backoffice.js').then((m) =>
                m.aprobarBeneficio(f.id),
              );
              if (error) {
                mostrarToast(`Error: ${error.message}`, { tono: 'peligro' });
                return;
              }
              mostrarToast('Beneficio aprobado.', { tono: 'exito' });
              await cargar(contenido, estadoVista);
            });

            const btnRechazar = document.createElement('button');
            btnRechazar.type = 'button';
            btnRechazar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
            btnRechazar.textContent = 'Rechazar';
            btnRechazar.addEventListener('click', async () => {
              const motivo = motivoRapido('Motivo del rechazo (obligatorio):');
              if (!motivo) {
                if (motivo === '') mostrarToast('El motivo es obligatorio.', { tono: 'alerta' });
                return;
              }
              const { error } = await rechazarBeneficio(f.id, motivo);
              if (error) {
                mostrarToast(`Error: ${error.message}`, { tono: 'peligro' });
                return;
              }
              mostrarToast('Beneficio rechazado.', { tono: 'alerta' });
              await cargar(contenido, estadoVista);
            });

            div.appendChild(btnAprobar);
            div.appendChild(btnRechazar);
            return div;
          },
        },
      ],
      filas: data ?? [],
    }),
  );
  destino.appendChild(
    crearPaginacion({
      total: count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargar(contenido, estadoVista);
      },
    }),
  );
}

function abrirModalCrear(contenedor) {
  const modal = document.createElement('div');
  modal.className = 'pf-modal-overlay';
  modal.innerHTML = `
    <div class="pf-tarjeta pf-modal" style="max-width:560px;">
      <h2 style="margin:0 0 16px;">Nuevo beneficio</h2>
      <form id="pf-form-beneficio" style="display:flex;flex-direction:column;gap:12px;">
        <div class="pf-campo">
          <label for="pf-ben-empresa">Empresa ID</label>
          <input id="pf-ben-empresa" type="text" required placeholder="UUID de la empresa" />
        </div>
        <div class="pf-campo">
          <label for="pf-ben-tipo">Tipo</label>
          <select id="pf-ben-tipo" required>
            ${TIPOS_BENEFICIO.map((t) => `<option value="${t}">${t}</option>`).join('')}
          </select>
        </div>
        <div class="pf-campo">
          <label for="pf-ben-descripcion">Descripción</label>
          <textarea id="pf-ben-descripcion" rows="2" required></textarea>
        </div>
        <div class="pf-fila" style="gap:12px;">
          <div class="pf-campo" style="flex:1;">
            <label for="pf-ben-inicio">Fecha inicio</label>
            <input id="pf-ben-inicio" type="datetime-local" required />
          </div>
          <div class="pf-campo" style="flex:1;">
            <label for="pf-ben-fin">Fecha fin</label>
            <input id="pf-ben-fin" type="datetime-local" required />
          </div>
        </div>
        <div class="pf-campo">
          <label for="pf-ben-motivo">Motivo (obligatorio)</label>
          <textarea id="pf-ben-motivo" rows="2" required></textarea>
        </div>
        <div class="pf-fila" style="justify-content:flex-end;gap:8px;">
          <button type="button" class="pf-boton pf-boton--secundario" id="pf-ben-cancelar">Cancelar</button>
          <button type="submit" class="pf-boton pf-boton--primario">Crear beneficio</button>
        </div>
      </form>
    </div>
  `;

  contenedor.appendChild(modal);

  modal.querySelector('#pf-ben-cancelar').addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });

  modal.querySelector('#pf-form-beneficio').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('[type="submit"]');
    btn.disabled = true;

    const { error } = await crearBeneficio({
      empresa_id: modal.querySelector('#pf-ben-empresa').value.trim(),
      tipo: modal.querySelector('#pf-ben-tipo').value,
      descripcion: modal.querySelector('#pf-ben-descripcion').value.trim(),
      fecha_inicio: new Date(modal.querySelector('#pf-ben-inicio').value).toISOString(),
      fecha_fin: new Date(modal.querySelector('#pf-ben-fin').value).toISOString(),
      motivo: modal.querySelector('#pf-ben-motivo').value.trim(),
      estado: 'aprobado',
    });

    btn.disabled = false;
    if (error) {
      mostrarToast(`Error: ${error.message}`, { tono: 'peligro' });
      return;
    }

    mostrarToast('Beneficio creado.', { tono: 'exito' });
    modal.remove();
  });
}
